import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Copy } from 'lucide-react';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { Switch } from '@repo/ui/src/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@repo/ui/src/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@repo/ui/src/components/ui/table';
import { cn } from '@repo/ui/src/lib/utils';
import type { AnalyzedRow, StudentImportAnalysis } from '@repo/shared-types';
import {
  buildCommitRows, findBatchDuplicateOf, isBatchDuplicateRow, type ImportClassContext, type RowEditState,
} from '../types';

const CLASS_ROW_CAP = 100;

interface PreviewStepProps {
  analysis: StudentImportAnalysis;
  rowEdits: Record<number, RowEditState>;
  onChange: (next: Record<number, RowEditState>) => void;
  onBack: () => void;
  onCommit: () => void;
  isPending: boolean;
  error: Error | null;
  /** THI-267 Task 13 — set when this wizard is embedded in AddMembersDialog's
   *  "Nhập từ Excel" tab. Turns on the Học phí column and the dup_warning
   *  "Dùng học viên đã có" / "Tạo mới" select (decision 8); this step's
   *  commit IS the confirm step in both modes. */
  classContext?: ImportClassContext | null;
}

// Fix wave (review C3) — hoisted to MODULE scope, `value`/`onChange` passed
// as props. Declared inside `PreviewStep`'s render body, this was a NEW
// function identity every keystroke: React saw a different component type
// at the same JSX position and unmounted/remounted the `<Input>`, which
// blurs it — so the fee column, the branch's headline addition to this
// screen, could not take a second digit without a re-click.
function RateCell({
  value, placeholder, onChange,
}: {
  value: string;
  placeholder: string | undefined;
  onChange: (v: string) => void;
}) {
  return (
    <TableCell className="align-top py-2">
      <Input
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-28 tabular"
      />
    </TableCell>
  );
}

export function PreviewStep({
  analysis, rowEdits, onChange, onBack, onCommit, isPending, error, classContext = null,
}: PreviewStepProps) {
  const { t } = useTranslation('studentImport');

  const groups = useMemo(() => {
    const ready: AnalyzedRow[] = [];
    const warning: AnalyzedRow[] = [];
    const needsFix: AnalyzedRow[] = [];
    const dupWarning: AnalyzedRow[] = [];
    for (const row of analysis.rows) {
      if (row.status === 'ready') ready.push(row);
      else if (row.status === 'warning') warning.push(row);
      else if (row.status === 'needs_fix') needsFix.push(row);
      else dupWarning.push(row);
    }
    return {
      ready, warning, needsFix, dupWarning,
    };
  }, [analysis.rows]);

  // Fix wave (review C2 copy) — which earlier row number a same-batch
  // duplicate matches, display-only (see `findBatchDuplicateOf`'s own doc).
  const batchDupOf = useMemo(() => findBatchDuplicateOf(analysis.rows), [analysis.rows]);

  // Same derivation as handleCommit — the count and the payload can't diverge.
  const commitCount = useMemo(
    () => buildCommitRows(analysis, rowEdits, classContext).length,
    [analysis, rowEdits, classContext],
  );
  // Fix wave (review I13) — the 100-row class-import cap was copy only;
  // nothing stopped a 140-row commit from reaching the server's own
  // `importCommitSchema` 400 after the operator had already fixed every
  // row. Gate the button and name the overage instead.
  const overCap = classContext !== null && commitCount > CLASS_ROW_CAP;

  const updateRow = (rowNum: number, patch: Partial<RowEditState>) => {
    const current = rowEdits[rowNum];
    if (!current) return;
    onChange({ ...rowEdits, [rowNum]: { ...current, ...patch } });
  };

  const colCount = classContext ? 6 : 5;
  const ratePlaceholder = classContext?.defaultRate != null ? String(classContext.defaultRate) : undefined;

  return (
    <div className="py-2 space-y-4">
      {/* Fix wave (review S6/3) — status-chip order matches the handoff
          frame: Sẵn sàng, Có thể trùng, Có cảnh báo, Thiếu dữ liệu. */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span className="inline-flex items-center gap-1.5 text-green-700">
          <CheckCircle2 className="h-4 w-4" /> {t('preview.ready', { count: groups.ready.length })}
        </span>
        <span className="inline-flex items-center gap-1.5 text-blue-700">
          <Copy className="h-4 w-4" /> {t('preview.dupWarning', { count: groups.dupWarning.length })}
        </span>
        <span className="inline-flex items-center gap-1.5 text-amber-700">
          <AlertTriangle className="h-4 w-4" /> {t('preview.warning', { count: groups.warning.length })}
        </span>
        <span className="inline-flex items-center gap-1.5 text-red-700">
          <AlertTriangle className="h-4 w-4" /> {t('preview.needsFix', { count: groups.needsFix.length })}
        </span>
      </div>

      {classContext && (
        <p className="text-xs text-surface-500">{t('preview.classCapNote')}</p>
      )}

      {analysis.summary.overflowDropped > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{t('overflowWarning', { count: analysis.summary.overflowDropped })}</span>
        </div>
      )}

      {/* Fix wave (review I15) — `overflow-x-auto` lives on the SAME element
          as the max-height/scroll, so the sticky header and the horizontal
          scrollbar share one scrollport instead of the table's own inner
          `overflow-auto` div owning the x-axis while this wrapper owns y
          (§6.13's exact trap: a sticky header can't stick to a box that
          itself scrolls). Below md this also drops to a card list so the
          six-column table never needs the horizontal scroll at all. */}
      <div className="hidden md:block max-h-[45vh] overflow-auto rounded-md border">
        <Table>
          <TableHeader className="sticky top-0 bg-card z-10">
            <TableRow>
              <TableHead className="w-12">{t('preview.col.row')}</TableHead>
              <TableHead>{t('preview.col.studentName')}</TableHead>
              <TableHead>{t('preview.col.payer')}</TableHead>
              {classContext && <TableHead className="w-28">{t('preview.col.fee')}</TableHead>}
              <TableHead className="w-32">{t('preview.col.status')}</TableHead>
              <TableHead className="text-right w-40">{t('preview.col.action')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.ready.map((row) => (
              <TableRow key={row.rowNum}>
                <TableCell className="text-surface-400">{row.rowNum}</TableCell>
                <TableCell className="font-medium">{row.mapped.studentName}</TableCell>
                <TableCell>{row.mapped.payerName || '—'}</TableCell>
                {classContext && (
                  <RateCell
                    value={rowEdits[row.rowNum]?.rate ?? ''}
                    placeholder={ratePlaceholder}
                    onChange={(v) => updateRow(row.rowNum, { rate: v })}
                  />
                )}
                <TableCell>
                  <Badge variant="default" className="bg-green-500">{t('preview.statusReady')}</Badge>
                </TableCell>
                <TableCell />
              </TableRow>
            ))}

            {groups.dupWarning.map((row) => {
              const edit = rowEdits[row.rowNum];
              if (!edit) return null;
              const isBatchDup = isBatchDuplicateRow(row, classContext);
              const resolvesToExisting = classContext !== null && edit.useExisting && row.existingStudentId !== null;
              return (
                <TableRow key={row.rowNum} className={cn(!classContext && !edit.kept && 'opacity-60')}>
                  <TableCell className="text-surface-400">{row.rowNum}</TableCell>
                  <TableCell className="font-medium">{row.mapped.studentName}</TableCell>
                  <TableCell>
                    {classContext && resolvesToExisting && row.existingPayer
                      ? t('preview.dupExistingPayer', { name: row.existingPayer.name, phone: row.existingPayer.phone ?? '—' })
                      : row.mapped.payerName || '—'}
                    {isBatchDup && batchDupOf.has(row.rowNum) ? (
                      <p className="text-[11px] text-blue-600 mt-1">
                        {t('preview.dupBatchNote', { row: batchDupOf.get(row.rowNum) })}
                      </p>
                    ) : row.issues.length > 0 && (
                      <p className="text-[11px] text-blue-600 mt-1">{row.issues.join('; ')}</p>
                    )}
                  </TableCell>
                  {classContext && (resolvesToExisting ? <TableCell /> : (
                    <RateCell
                      value={edit.rate}
                      placeholder={ratePlaceholder}
                      onChange={(v) => updateRow(row.rowNum, { rate: v })}
                    />
                  ))}
                  <TableCell>
                    <Badge variant="outline" className="border-blue-400 text-blue-700">
                      {t('preview.statusDup')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {classContext ? (
                      <Select
                        value={edit.useExisting ? 'existing' : 'new'}
                        onValueChange={(v) => updateRow(row.rowNum, { useExisting: v === 'existing' })}
                      >
                        <SelectTrigger className="h-8 ml-auto w-[180px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="existing">
                            {isBatchDup ? t('preview.dupSkipBatch') : t('preview.dupUseExisting')}
                          </SelectItem>
                          <SelectItem value="new">{t('preview.dupCreateNew')}</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <label className="inline-flex items-center gap-1.5 text-xs text-surface-600 cursor-pointer">
                        {t('preview.keepAnyway')}
                        <Switch
                          checked={edit.kept}
                          onCheckedChange={(v) => updateRow(row.rowNum, { kept: v })}
                        />
                      </label>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}

            {groups.warning.map((row) => (
              <TableRow key={row.rowNum}>
                <TableCell className="text-surface-400 align-top">{row.rowNum}</TableCell>
                <TableCell className="font-medium align-top">
                  {row.mapped.studentName}
                  {/* The whole point of this status: the complaint is on screen
                      while the file can still be fixed and re-uploaded. */}
                  {row.issues.length > 0 && (
                    <p className="text-[11px] text-amber-600 mt-1">{row.issues.join('; ')}</p>
                  )}
                </TableCell>
                <TableCell className="align-top">{row.mapped.payerName || '—'}</TableCell>
                {classContext && (
                  <RateCell
                    value={rowEdits[row.rowNum]?.rate ?? ''}
                    placeholder={ratePlaceholder}
                    onChange={(v) => updateRow(row.rowNum, { rate: v })}
                  />
                )}
                <TableCell className="align-top">
                  <Badge variant="outline" className="border-amber-400 text-amber-700">
                    {t('preview.statusWarning')}
                  </Badge>
                </TableCell>
                <TableCell />
              </TableRow>
            ))}

            {groups.needsFix.map((row) => {
              const edit = rowEdits[row.rowNum];
              if (!edit) return null;
              return (
                <TableRow key={row.rowNum} className={cn(edit.excluded && 'opacity-50')}>
                  <TableCell className="text-surface-400 align-top pt-3">{row.rowNum}</TableCell>
                  <TableCell className="align-top py-2">
                    <Input
                      value={edit.studentName}
                      disabled={edit.excluded}
                      placeholder={t('preview.studentNamePlaceholder')}
                      onChange={(e) => updateRow(row.rowNum, { studentName: e.target.value })}
                      className="h-8"
                    />
                    {row.issues.length > 0 && (
                      <p className="text-[11px] text-red-600 mt-1">{row.issues.join('; ')}</p>
                    )}
                  </TableCell>
                  <TableCell className="align-top py-2">
                    <div className="grid gap-1">
                      <Input
                        value={edit.payerName}
                        disabled={edit.excluded}
                        placeholder={t('preview.payerNamePlaceholder')}
                        onChange={(e) => updateRow(row.rowNum, { payerName: e.target.value })}
                        className="h-8"
                      />
                      <Input
                        value={edit.payerPhone}
                        disabled={edit.excluded}
                        placeholder={t('preview.payerPhonePlaceholder')}
                        onChange={(e) => updateRow(row.rowNum, { payerPhone: e.target.value })}
                        className="h-8"
                      />
                    </div>
                  </TableCell>
                  {classContext && (
                    <RateCell
                      value={edit.rate}
                      placeholder={ratePlaceholder}
                      onChange={(v) => updateRow(row.rowNum, { rate: v })}
                    />
                  )}
                  <TableCell className="align-top pt-3">
                    <Badge variant="outline" className="border-red-400 text-red-700">
                      {t('preview.statusNeedsFix')}
                    </Badge>
                  </TableCell>
                  <TableCell className="align-top pt-2.5 text-right">
                    <label className="inline-flex items-center gap-1.5 text-xs text-surface-600 cursor-pointer">
                      {t('preview.exclude')}
                      <Switch
                        checked={edit.excluded}
                        onCheckedChange={(v) => updateRow(row.rowNum, { excluded: v })}
                      />
                    </label>
                  </TableCell>
                </TableRow>
              );
            })}

            {analysis.rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={colCount} className="h-20 text-center text-muted-foreground">
                  {t('preview.empty')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Phone (below md): one card per row, no horizontal scroll (I15). */}
      <div className="flex md:hidden flex-col gap-2 max-h-[55vh] overflow-y-auto">
        {analysis.rows.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('preview.empty')}</p>
        )}
        {analysis.rows.map((row) => {
          const edit = rowEdits[row.rowNum];
          if (!edit) return null;
          const isDup = row.status === 'dup_warning';
          const isBatchDup = isDup && isBatchDuplicateRow(row, classContext);
          const resolvesToExisting = isDup && classContext !== null && edit.useExisting && row.existingStudentId !== null;
          const statusBadge = row.status === 'ready'
            ? <Badge variant="default" className="bg-green-500">{t('preview.statusReady')}</Badge>
            : isDup
              ? <Badge variant="outline" className="border-blue-400 text-blue-700">{t('preview.statusDup')}</Badge>
              : row.status === 'warning'
                ? <Badge variant="outline" className="border-amber-400 text-amber-700">{t('preview.statusWarning')}</Badge>
                : <Badge variant="outline" className="border-red-400 text-red-700">{t('preview.statusNeedsFix')}</Badge>;
          return (
            <div
              key={row.rowNum}
              className={cn(
                'flex flex-col gap-1.5 rounded-lg border border-surface-200 p-3',
                (row.status === 'needs_fix' && edit.excluded) && 'opacity-50',
                (!classContext && isDup && !edit.kept) && 'opacity-60',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-surface-400">{t('preview.col.row')} {row.rowNum}</span>
                {statusBadge}
              </div>
              {row.status === 'needs_fix' ? (
                <div className="grid gap-1">
                  <Input
                    value={edit.studentName}
                    disabled={edit.excluded}
                    placeholder={t('preview.studentNamePlaceholder')}
                    onChange={(e) => updateRow(row.rowNum, { studentName: e.target.value })}
                    className="h-8"
                  />
                  <Input
                    value={edit.payerName}
                    disabled={edit.excluded}
                    placeholder={t('preview.payerNamePlaceholder')}
                    onChange={(e) => updateRow(row.rowNum, { payerName: e.target.value })}
                    className="h-8"
                  />
                  <Input
                    value={edit.payerPhone}
                    disabled={edit.excluded}
                    placeholder={t('preview.payerPhonePlaceholder')}
                    onChange={(e) => updateRow(row.rowNum, { payerPhone: e.target.value })}
                    className="h-8"
                  />
                </div>
              ) : (
                <>
                  <span className="font-medium text-sm">{row.mapped.studentName}</span>
                  <span className="text-[13px] text-surface-600">
                    {classContext && resolvesToExisting && row.existingPayer
                      ? t('preview.dupExistingPayer', { name: row.existingPayer.name, phone: row.existingPayer.phone ?? '—' })
                      : row.mapped.payerName || '—'}
                  </span>
                </>
              )}
              {isBatchDup && batchDupOf.has(row.rowNum) ? (
                <p className="m-0 text-[11px] text-blue-600">{t('preview.dupBatchNote', { row: batchDupOf.get(row.rowNum) })}</p>
              ) : row.issues.length > 0 && (
                <p className={cn('m-0 text-[11px]', row.status === 'needs_fix' ? 'text-red-600' : isDup ? 'text-blue-600' : 'text-amber-600')}>
                  {row.issues.join('; ')}
                </p>
              )}
              {/* Re-review fix (new defect 2): needs_fix rows get the fee
                  field too, matching the desktop table's RateCell — a
                  needs_fix row with no class/subject default rate had no
                  way to set one on a phone, so it 400'd RATE_REQUIRED at
                  commit with nothing on screen to fix. */}
              {classContext && !(isDup && resolvesToExisting) && (
                <label className="flex flex-col gap-0.5 text-xs text-surface-600">
                  {t('preview.col.fee')}
                  <Input
                    value={edit.rate}
                    placeholder={ratePlaceholder}
                    onChange={(e) => updateRow(row.rowNum, { rate: e.target.value })}
                    className="h-8 w-28 tabular"
                  />
                </label>
              )}
              {isDup && (
                classContext ? (
                  <Select
                    value={edit.useExisting ? 'existing' : 'new'}
                    onValueChange={(v) => updateRow(row.rowNum, { useExisting: v === 'existing' })}
                  >
                    <SelectTrigger className="h-8 w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="existing">
                        {isBatchDup ? t('preview.dupSkipBatch') : t('preview.dupUseExisting')}
                      </SelectItem>
                      <SelectItem value="new">{t('preview.dupCreateNew')}</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <label className="inline-flex items-center gap-1.5 text-xs text-surface-600 cursor-pointer">
                    {t('preview.keepAnyway')}
                    <Switch checked={edit.kept} onCheckedChange={(v) => updateRow(row.rowNum, { kept: v })} />
                  </label>
                )
              )}
              {row.status === 'needs_fix' && (
                <label className="inline-flex items-center gap-1.5 text-xs text-surface-600 cursor-pointer">
                  {t('preview.exclude')}
                  <Switch checked={edit.excluded} onCheckedChange={(v) => updateRow(row.rowNum, { excluded: v })} />
                </label>
              )}
            </div>
          );
        })}
      </div>

      {overCap && (
        <p className="text-sm text-red-600">{t('preview.classCapExceeded', { count: commitCount, over: commitCount - CLASS_ROW_CAP })}</p>
      )}
      {error && <p className="text-sm text-red-600">{error.message || t('preview.commitError')}</p>}

      <div className="flex items-center justify-between pt-3 border-t">
        <Button type="button" variant="outline" onClick={onBack} disabled={isPending}>
          {t('preview.back')}
        </Button>
        <div className="flex items-center gap-3">
          <span className="text-sm text-surface-600">{t('preview.willImport', { count: commitCount })}</span>
          <Button type="button" onClick={onCommit} disabled={isPending || commitCount === 0 || overCap}>
            {isPending ? t('preview.committing') : t('preview.commit')}
          </Button>
        </div>
      </div>
    </div>
  );
}
